import React, { useState, useEffect } from "react";
import axios from 'axios';

const SuperAdminPanel = ({ onBack }) => {
  const [adminPassword, setAdminPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [allListings, setAllListings] = useState([]);
  const [pendingListings, setPendingListings] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('listings');
  const [selectedListing, setSelectedListing] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const ADMIN_PASSWORD = 'ngng';

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated]);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('access_token') || localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  const loadData = async () => {
    setLoading(true);
    setMessage('');
    
    try {
      console.log('Loading admin data...');
      
      // Load pending listings with better error handling
      let pendingData = [];
      try {
        const pendingResponse = await axios.get('https://app.akiya2.com/agents/admin/pending-listings', {
          headers: getAuthHeaders()
        });
        console.log('Pending listings response:', pendingResponse.data);
        
        // Handle different response structures
        if (pendingResponse.data?.listings) {
          pendingData = pendingResponse.data.listings;
        } else if (Array.isArray(pendingResponse.data)) {
          pendingData = pendingResponse.data;
        } else {
          pendingData = [];
        }
        
        setPendingListings(pendingData);
      } catch (pendingError) {
        console.error('Error loading pending listings:', pendingError);
        setPendingListings([]);
        setMessage('Warning: Could not load pending listings');
      }

      // Load all listings
      let allData = [];
      try {
        const allListingsResponse = await axios.get('https://app.akiya2.com/agents/admin/all-listings', {
          headers: getAuthHeaders()
        });
        console.log('All listings response:', allListingsResponse.data);
        
        if (allListingsResponse.data?.listings) {
          allData = allListingsResponse.data.listings;
        } else if (Array.isArray(allListingsResponse.data)) {
          allData = allListingsResponse.data;
        } else {
          allData = pendingData; // Fallback to pending data
        }
        
        setAllListings(allData);
      } catch (allListingsError) {
        console.warn('Could not load all listings, using pending listings as fallback');
        setAllListings(pendingData);
      }

      // Load all users/agents
      let userData = [];
      try {
        const usersResponse = await axios.get('https://app.akiya2.com/agents/admin/all-users', {
          headers: getAuthHeaders()
        });
        console.log('Users response:', usersResponse.data);
        
        if (usersResponse.data?.users) {
          userData = usersResponse.data.users;
        } else if (usersResponse.data?.agents) {
          userData = usersResponse.data.agents;
        } else if (Array.isArray(usersResponse.data)) {
          userData = usersResponse.data;
        }
        
        setAllUsers(userData.filter(user => user.isAgent === true));

      } catch (usersError) {
        console.warn('Could not load users data:', usersError);
        setAllUsers([]);
      }
      
      if (!message) {
        setMessage(`Loaded ${allData.length} listings, ${pendingData.length} pending, ${userData.length} users`);
      }
      
    } catch (error) {
      console.error('Error loading admin data:', error);
      setMessage(`Error loading data: ${error.response?.data?.detail || error.message}`);
      
      // Set empty arrays as fallback
      setPendingListings([]);
      setAllListings([]);
      setAllUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminLogin = (e) => {
    e.preventDefault();
    if (adminPassword === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setMessage('Successfully authenticated as Super Admin');
    } else {
      setMessage('Incorrect password!');
    }
  };

  const handleDeleteListing = async (listingId) => {
    if (window.confirm('Are you sure you want to delete this listing permanently?')) {
      try {
        setLoading(true);
        setMessage('Deleting listing...');
        
        await axios.delete(`https://app.akiya2.com/agents/admin/delete-listing/${listingId}`, {
          headers: getAuthHeaders()
        });
        
        setMessage('Listing deleted successfully');
        await loadData(); // Reload data
      } catch (error) {
        console.error('Error deleting listing:', error);
        setMessage(`Error deleting listing: ${error.response?.data?.detail || error.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditListing = (listing) => {
    setSelectedListing({...listing});
    setShowEditModal(true);
  };

  const handleViewDetails = (listing) => {
    setSelectedListing(listing);
    setShowDetailModal(true);
  };

  const handleUpdateListing = async () => {
    try {
      setLoading(true);
      setMessage('Updating listing...');
      
      await axios.put(`https://app.akiya2.com/agents/edit-listing/${selectedListing.property_id}`, {
        ...selectedListing,
        updatedAt: new Date().toISOString()
      }, {
        headers: getAuthHeaders()
      });
      
      setMessage('Listing updated successfully');
      setShowEditModal(false);
      setSelectedListing(null);
      await loadData(); // Reload data
    } catch (error) {
      console.error('Error updating listing:', error);
      setMessage(`Error updating listing: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveListing = async (listingId) => {
    try {
      setLoading(true);
      setMessage('Approving listing...');
      
      await axios.post('https://app.akiya2.com/agents/admin/approve-listing', {
        property_id: listingId,
        action: 'approve'
      }, {
        headers: getAuthHeaders()
      });
      
      setMessage('Listing approved successfully');
      await loadData(); // Reload data
    } catch (error) {
      console.error('Error approving listing:', error);
      setMessage(`Error approving listing: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRejectListing = async (listingId) => {
    if (window.confirm('Are you sure you want to reject this listing?')) {
      try {
        setLoading(true);
        setMessage('Rejecting listing...');
        
        await axios.post('https://app.akiya2.com/agents/admin/approve-listing', {
          property_id: listingId,
          action: 'reject'
        }, {
          headers: getAuthHeaders()
        });
        
        setMessage('Listing rejected successfully');
        await loadData(); // Reload data
      } catch (error) {
        console.error('Error rejecting listing:', error);
        setMessage(`Error rejecting listing: ${error.response?.data?.detail || error.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBulkApprove = async () => {
    const pendingItems = pendingListings.filter(l => l.status === 'pending_approval');
    if (pendingItems.length === 0) {
      setMessage('No pending listings to approve');
      return;
    }

    if (window.confirm(`Are you sure you want to approve all ${pendingItems.length} pending listings?`)) {
      try {
        setLoading(true);
        setMessage('Bulk approving listings...');
        
        const promises = pendingItems.map(listing => 
          axios.post('https://app.akiya2.com/agents/admin/approve-listing', {
            property_id: listing.property_id,
            action: 'approve'
          }, {
            headers: getAuthHeaders()
          })
        );

        await Promise.all(promises);
        setMessage(`Successfully approved ${pendingItems.length} listings`);
        await loadData();
      } catch (error) {
        console.error('Error in bulk approval:', error);
        setMessage(`Error in bulk approval: ${error.response?.data?.detail || error.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending_approval':
        return 'bg-yellow-100 text-yellow-800';
      case 'active':
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    // Remove commas and parse as number
    const numPrice = parseInt(price.toString().replace(/,/g, ''));
    if (isNaN(numPrice)) return price;
    return numPrice.toLocaleString();
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center p-4">
        <link 
          rel="stylesheet" 
          href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" 
        />
        
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-shield-alt text-white text-3xl"></i>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Super Admin</h1>
            <p className="text-gray-600">Enter admin password to continue</p>
          </div>

          <form onSubmit={handleAdminLogin} className="space-y-6">
            <div>
              <label className="block font-medium mb-2 text-gray-700">Admin Password</label>
              <input 
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Enter admin password"
                required
              />
            </div>

            {message && (
              <div className={`p-3 rounded-lg text-sm ${
                message.includes('Error') || message.includes('Incorrect') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
              }`}>
                {message}
              </div>
            )}

            <button 
              type="submit"
              className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white py-4 rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-300 shadow-lg hover:shadow-xl font-medium text-lg"
            >
              <i className="fas fa-sign-in-alt mr-2"></i>
              Access Admin Panel
            </button>

            <button 
              type="button"
              onClick={onBack}
              className="w-full bg-gray-200 text-gray-700 py-4 rounded-xl hover:bg-gray-300 transition-colors font-medium"
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Back to Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <link 
        rel="stylesheet" 
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" 
      />
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 mb-1">Super Admin Panel</h1>
              <p className="text-gray-600">Manage all listings and agents</p>
              {message && (
                <div className={`mt-2 p-2 rounded-lg text-sm ${
                  message.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                }`}>
                  {message}
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center">
                  <i className="fas fa-shield-alt text-white"></i>
                </div>
                <div>
                  <p className="font-medium text-gray-800">Super Admin</p>
                  <p className="text-sm text-gray-600">System Administrator</p>
                </div>
              </div>
              
              <button 
                onClick={() => {setMessage(''); loadData();}}
                className="px-4 py-2 bg-blue-100 text-blue-700 rounded-xl hover:bg-blue-200 transition-colors mr-2"
                disabled={loading}
              >
                <i className="fas fa-sync-alt mr-2"></i>
                {loading ? 'Loading...' : 'Refresh'}
              </button>
              
              <button 
                onClick={onBack}
                className="px-4 py-2 bg-red-100 text-red-700 rounded-xl hover:bg-red-200 transition-colors"
              >
                <i className="fas fa-sign-out-alt mr-2"></i>
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-8">
            {[
              { id: 'listings', label: 'All Listings', icon: 'fas fa-home', count: allListings.length },
              { id: 'agents', label: 'Agents', icon: 'fas fa-users', count: allUsers.length },
              { id: 'pending', label: 'Pending Approval', icon: 'fas fa-clock', count: pendingListings.filter(l => l.status === 'pending_approval').length }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-4 px-2 border-b-2 font-medium text-sm transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className={`${tab.icon} mr-2`}></i>
                {tab.label}
                <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {activeTab === 'listings' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">All Property Listings</h2>
              <div className="text-sm text-gray-600">
                Total: {allListings.length} listings
              </div>
            </div>

            {allListings.length === 0 ? (
              <div className="text-center py-12">
                <i className="fas fa-home text-gray-400 text-6xl mb-4"></i>
                <h3 className="text-lg font-medium text-gray-600 mb-2">No listings found</h3>
                <p className="text-gray-500">There are currently no property listings in the system.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {allListings.map((listing) => (
                  <div key={listing.property_id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center relative">
                      {listing.Images && listing.Images.length > 0 ? (
                        <img 
                          src={listing.Images[0]} 
                          alt={listing.Address}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="fas fa-building text-gray-400 text-4xl"></i>
                      </div>
                      
                      <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(listing.status)}`}>
                        {listing.status === 'pending_approval' ? 'Pending' : 
                         listing.status === 'active' ? 'Active' :
                         listing.status === 'rejected' ? 'Rejected' : listing.status}
                      </div>
                      
                      <div className="absolute top-4 right-4 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                        {listing.agentId || 'No Agent'}
                      </div>
                    </div>
                    
                    <div className="p-6">
                      <h3 className="font-bold text-gray-800 mb-2">{listing.Address}</h3>
                      <p className="text-gray-600 mb-1">{listing.Prefecture}</p>
                      <p className="text-gray-500 text-sm mb-3">{listing.Municipality}</p>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center text-red-600">
                          <i className="fas fa-yen-sign w-5 mr-2"></i>
                          <span className="font-bold text-lg">¥{formatPrice(listing.Price)}</span>
                        </div>
                        <div className="flex items-center space-x-4 text-gray-600 text-sm">
                          <span><i className="fas fa-calendar mr-1"></i>{listing.Year_of_Construction}</span>
                          <span><i className="fas fa-home mr-1"></i>{listing.Room_Type}</span>
                        </div>
                        <div className="flex items-center space-x-4 text-gray-600 text-sm">
                          <span><i className="fas fa-expand-arrows-alt mr-1"></i>{listing.Area_of_House}m²</span>
                          <span><i className="fas fa-layer-group mr-1"></i>{listing.No_of_Floors} floors</span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-2">
                        <button 
                          onClick={() => handleViewDetails(listing)}
                          className="flex items-center justify-center py-2 px-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
                        >
                          <i className="fas fa-eye mr-1"></i>
                          View
                        </button>
                        
                        <button 
                          onClick={() => handleEditListing(listing)}
                          className="flex items-center justify-center py-2 px-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                        >
                          <i className="fas fa-edit mr-1"></i>
                          Edit
                        </button>
                        
                        <button 
                          onClick={() => handleDeleteListing(listing.property_id)}
                          className="flex items-center justify-center py-2 px-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm"
                        >
                          <i className="fas fa-trash mr-1"></i>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'agents' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">All Agents</h2>
              <div className="text-sm text-gray-600">
                Total: {allUsers.length} agents
              </div>
            </div>

            {allUsers.length === 0 ? (
              <div className="text-center py-12">
                <i className="fas fa-users text-gray-400 text-6xl mb-4"></i>
                <h3 className="text-lg font-medium text-gray-600 mb-2">No agents found</h3>
                <p className="text-gray-500">There are currently no registered agents in the system.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {allUsers.map((user) => (
                  <div key={user.id || user.agent_id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#10c9c0] to-[#0eb1a9] rounded-full flex items-center justify-center overflow-hidden">
                        {user.profileImage || user.image_url ? (
                          <img src={user.profileImage || user.image_url} alt="Profile" className="w-full h-full object-cover" />
                        ) : (
                          <i className="fas fa-user text-white text-xl"></i>
                        )}
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-800">{user.name || `${user.first_name} ${user.last_name}`}</h3>
                        <p className="text-gray-600 text-sm">{user.agentId || user.agent_id}</p>
                        <p className="text-gray-500 text-xs">{user.agentType || 'Agent'}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center text-gray-600">
                        <i className="fas fa-envelope w-5 mr-2"></i>
                        <span>{user.email}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <i className="fas fa-phone w-5 mr-2"></i>
                        <span>{user.phone || 'N/A'}</span>
                      </div>
                      {user.company && (
                        <div className="flex items-center text-gray-600">
                          <i className="fas fa-building w-5 mr-2"></i>
                          <span>{user.company}</span>
                        </div>
                      )}
                      <div className="flex items-center text-gray-600">
                        <i className="fas fa-home w-5 mr-2"></i>
                        <span>{allListings.filter(l => l.agentId === (user.agentId || user.agent_id)).length} listings</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'pending' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">Pending Approval Listings</h2>
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-600">
                  Total: {pendingListings.filter(l => l.status === 'pending_approval').length} pending items
                </div>
                {pendingListings.filter(l => l.status === 'pending_approval').length > 0 && (
                  <button
                    onClick={handleBulkApprove}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
                    disabled={loading}
                  >
                    <i className="fas fa-check-double mr-2"></i>
                    Approve All
                  </button>
                )}
              </div>
            </div>

            {pendingListings.filter(l => l.status === 'pending_approval').length === 0 ? (
              <div className="text-center py-12">
                <i className="fas fa-clock text-gray-400 text-6xl mb-4"></i>
                <h3 className="text-lg font-medium text-gray-600 mb-2">No pending listings</h3>
                <p className="text-gray-500">All listings have been reviewed.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {pendingListings.filter(l => l.status === 'pending_approval').map((listing) => (
                  <div key={listing.property_id} className="bg-white rounded-2xl shadow-sm border border-yellow-200 p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-800 mb-1">{listing.Address}</h3>
                        <p className="text-gray-600">{listing.Prefecture}, {listing.Municipality}</p>
                        <p className="text-sm text-blue-600">Agent: {listing.agentId}</p>
                        <p className="text-xs text-gray-500">
                          Created: {new Date(listing.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button 
                          onClick={() => handleViewDetails(listing)}
                          className="px-3 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
                        >
                          <i className="fas fa-eye mr-1"></i>
                          Details
                        </button>
                        <button 
                          onClick={() => handleApproveListing(listing.property_id)}
                          className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
                          disabled={loading}
                        >
                          <i className="fas fa-check mr-1"></i>
                          Approve
                        </button>
                        <button 
                          onClick={() => handleRejectListing(listing.property_id)}
                          className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm"
                          disabled={loading}
                        >
                          <i className="fas fa-times mr-1"></i>
                          Reject
                        </button>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="text-red-600 font-bold text-lg">¥{formatPrice(listing.Price)}</div>
                      <div className="text-sm text-gray-600">
                        {listing.Year_of_Construction} • {listing.Room_Type} • {listing.Empty_or_Not}
                      </div>
                      <div className="text-sm text-gray-600">
                        House: {listing.Area_of_House}m² • Land: {listing.Area_of_Land}m² • {listing.No_of_Floors} floors
                      </div>
                      {listing.description && (
                        <div className="text-sm text-gray-700 bg-gray-50 p-2 rounded">
                          <strong>Description:</strong> {listing.description}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Detail Modal */}
      {showDetailModal && selectedListing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-800">Listing Details</h3>
              <button 
                onClick={() => setShowDetailModal(false)}
                className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center"
              >
                <i className="fas fa-times text-gray-600"></i>
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Basic Information</h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>Property ID:</strong> {selectedListing.property_id}</p>
                    <p><strong>Address:</strong> {selectedListing.Address}</p>
                    <p><strong>Prefecture:</strong> {selectedListing.Prefecture}</p>
                    <p><strong>Municipality:</strong> {selectedListing.Municipality}</p>
                    <p><strong>Price:</strong> ¥{formatPrice(selectedListing.Price)}</p>
                    <p><strong>Status:</strong> <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(selectedListing.status)}`}>{selectedListing.Empty_or_Not}</span></p>
                    <p><strong>Room Type:</strong> {selectedListing.Room_Type}</p>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Property Details</h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>Year Built:</strong> {selectedListing.Year_of_Construction}</p>
                    <p><strong>House Area:</strong> {selectedListing.Area_of_House}m²</p>
                    <p><strong>Land Area:</strong> {selectedListing.Area_of_Land}m²</p>
                    <p><strong>Number of Floors:</strong> {selectedListing.No_of_Floors}</p>
                    <p><strong>Contact Info:</strong> {selectedListing.Contact_Info}</p>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Agent & Listing Info</h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>Agent ID:</strong> {selectedListing.agentId}</p>
                    <p><strong>Listing Status:</strong> <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(selectedListing.status)}`}>{selectedListing.status}</span></p>
                    <p><strong>Featured:</strong> {selectedListing.featured ? 'Yes' : 'No'}</p>
                    <p><strong>Created:</strong> {new Date(selectedListing.createdAt).toLocaleString()}</p>
                    <p><strong>Updated:</strong> {new Date(selectedListing.updatedAt).toLocaleString()}</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                {selectedListing.Images && selectedListing.Images.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2">Images ({selectedListing.Images.length})</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {selectedListing.Images.map((image, index) => (
                        <img 
                          key={index}
                          src={image} 
                          alt={`Property ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => window.open(image, '_blank')}
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTgiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBOb3QgRm91bmQ8L3RleHQ+PC9zdmc+';
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedListing.description && (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2">Description</h4>
                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                      {selectedListing.description}
                    </p>
                  </div>
                )}
                
                {selectedListing.Coordinates && (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2">Location</h4>
                    <p className="text-sm text-gray-600">
                      Coordinates: {selectedListing.Coordinates[0]}, {selectedListing.Coordinates[1]}
                    </p>
                    <button 
                      onClick={() => window.open(`https://www.google.com/maps?q=${selectedListing.Coordinates[0]},${selectedListing.Coordinates[1]}`, '_blank')}
                      className="mt-2 text-blue-600 hover:text-blue-800 underline text-sm"
                    >
                      <i className="fas fa-map-marker-alt mr-1"></i>
                      View on Google Maps
                    </button>
                  </div>
                )}
                
                {selectedListing.Link_to_Website && (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2">Website</h4>
                    <a 
                      href={selectedListing.Link_to_Website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline text-sm"
                    >
                      <i className="fas fa-external-link-alt mr-1"></i>
                      {selectedListing.Link_to_Website}
                    </a>
                  </div>
                )}
                
                {selectedListing.supportingDocType && selectedListing.supportingDocContent && (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2">Supporting Documents</h4>
                    <div className="text-sm text-gray-600">
                      <p><strong>Type:</strong> {selectedListing.supportingDocType}</p>
                      {selectedListing.supportingDocType === 'link' ? (
                        <a 
                          href={selectedListing.supportingDocContent} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline"
                        >
                          {selectedListing.supportingDocContent}
                        </a>
                      ) : (
                        <p>{selectedListing.supportingDocContent}</p>
                      )}
                    </div>
                  </div>
                )}
                
                {selectedListing.status === 'pending_approval' && (
                  <div className="flex space-x-3 pt-4">
                    <button 
                      onClick={() => {
                        handleApproveListing(selectedListing.property_id);
                        setShowDetailModal(false);
                      }}
                      className="flex-1 bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 transition-colors font-medium"
                      disabled={loading}
                    >
                      <i className="fas fa-check mr-2"></i>
                      Approve Listing
                    </button>
                    <button 
                      onClick={() => {
                        handleRejectListing(selectedListing.property_id);
                        setShowDetailModal(false);
                      }}
                      className="flex-1 bg-red-500 text-white py-2 px-4 rounded-lg hover:bg-red-600 transition-colors font-medium"
                      disabled={loading}
                    >
                      <i className="fas fa-times mr-2"></i>
                      Reject Listing
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedListing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-800">Edit Listing</h3>
              <button 
                onClick={() => setShowEditModal(false)}
                className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center"
              >
                <i className="fas fa-times text-gray-600"></i>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block font-medium mb-2 text-gray-700">Address</label>
                <input 
                  type="text"
                  value={selectedListing.Address || ''}
                  onChange={(e) => setSelectedListing({...selectedListing, Address: e.target.value})}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-medium mb-2 text-gray-700">Prefecture</label>
                  <input 
                    type="text"
                    value={selectedListing.Prefecture || ''}
                    onChange={(e) => setSelectedListing({...selectedListing, Prefecture: e.target.value})}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block font-medium mb-2 text-gray-700">Municipality</label>
                  <input 
                    type="text"
                    value={selectedListing.Municipality || ''}
                    onChange={(e) => setSelectedListing({...selectedListing, Municipality: e.target.value})}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-medium mb-2 text-gray-700">Price (¥)</label>
                  <input 
                    type="text"
                    value={selectedListing.Price || ''}
                    onChange={(e) => setSelectedListing({...selectedListing, Price: e.target.value})}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block font-medium mb-2 text-gray-700">Year of Construction</label>
                  <input 
                    type="text"
                    value={selectedListing.Year_of_Construction || ''}
                    onChange={(e) => setSelectedListing({...selectedListing, Year_of_Construction: e.target.value})}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-medium mb-2 text-gray-700">Property Status</label>
                  <select 
                    value={selectedListing.Empty_or_Not || ''}
                    onChange={(e) => setSelectedListing({...selectedListing, Empty_or_Not: e.target.value})}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Sale">For Sale</option>
                    <option value="Rental">For Rental</option>
                    <option value="Sold">Sold</option>
                    <option value="Rented">Rented</option>
                  </select>
                </div>
                
                <div>
                  <label className="block font-medium mb-2 text-gray-700">Room Type</label>
                  <select 
                    value={selectedListing.Room_Type || ''}
                    onChange={(e) => setSelectedListing({...selectedListing, Room_Type: e.target.value})}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Room Type</option>
                    <option value="1K">1K</option>
                    <option value="1DK">1DK</option>
                    <option value="1LDK">1LDK</option>
                    <option value="2K">2K</option>
                    <option value="2DK">2DK</option>
                    <option value="2LDK">2LDK</option>
                    <option value="3K">3K</option>
                    <option value="3DK">3DK</option>
                    <option value="3LDK">3LDK</option>
                    <option value="4DK">4DK</option>
                    <option value="4LDK+">4LDK+</option>
                    <option value="House">House</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block font-medium mb-2 text-gray-700">House Area (m²)</label>
                  <input 
                    type="text"
                    value={selectedListing.Area_of_House || ''}
                    onChange={(e) => setSelectedListing({...selectedListing, Area_of_House: e.target.value})}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block font-medium mb-2 text-gray-700">Land Area (m²)</label>
                  <input 
                    type="text"
                    value={selectedListing.Area_of_Land || ''}
                    onChange={(e) => setSelectedListing({...selectedListing, Area_of_Land: e.target.value})}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block font-medium mb-2 text-gray-700">Number of Floors</label>
                  <input 
                    type="text"
                    value={selectedListing.No_of_Floors || ''}
                    onChange={(e) => setSelectedListing({...selectedListing, No_of_Floors: e.target.value})}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block font-medium mb-2 text-gray-700">Contact Information</label>
                <input 
                  type="text"
                  value={selectedListing.Contact_Info || ''}
                  onChange={(e) => setSelectedListing({...selectedListing, Contact_Info: e.target.value})}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block font-medium mb-2 text-gray-700">Description</label>
                <textarea 
                  value={selectedListing.description || ''}
                  onChange={(e) => setSelectedListing({...selectedListing, description: e.target.value})}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-24"
                  placeholder="Property description..."
                />
              </div>
              
              <div>
                <label className="block font-medium mb-2 text-gray-700">Website Link</label>
                <input 
                  type="text"
                  value={selectedListing.Link_to_Website || ''}
                  onChange={(e) => setSelectedListing({...selectedListing, Link_to_Website: e.target.value})}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://..."
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-medium mb-2 text-gray-700">Listing Status</label>
                  <select 
                    value={selectedListing.status || 'pending_approval'}
                    onChange={(e) => setSelectedListing({...selectedListing, status: e.target.value})}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="pending_approval">Pending Approval</option>
                    <option value="approved">Approved</option>
                    <option value="active">Active</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                
                <div>
                  <label className="block font-medium mb-2 text-gray-700">Featured</label>
                  <select 
                    value={selectedListing.featured ? 'true' : 'false'}
                    onChange={(e) => setSelectedListing({...selectedListing, featured: e.target.value === 'true'})}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>
              </div>
              
              <div className="flex space-x-4 pt-6">
                <button 
                  onClick={handleUpdateListing}
                  className="flex-1 bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600 transition-colors font-medium"
                  disabled={loading}
                >
                  <i className="fas fa-save mr-2"></i>
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
                <button 
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminPanel;